import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { AccordionSection } from "../../../components/AccordionSection";
import { useCompanyFilterActions, useCompanyFilterSlice } from "../../../hooks";
import { useDebouncedValue } from "../../../utils/useDebouncedValue";
import { MAX_INTENT_TOPICS, type CachedTopic } from "../slice";
import { useSnackbar } from "@/components/Snackbar";
import {
  CollectionSelect,
  type CollectionSelectOption,
} from "@/components/CollectionSelect/CollectionSelect";
import {
  useGetIntentTopicCollectionTreeQuery,
  useSearchIntentTopicsQuery,
} from "@/generated/taxonomy";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import { TopicUploadDialog } from "../components/TopicUploadDialog";
import { useBulkTopicOperations } from "../hooks/useBulkTopicOperations";
import {
  type CollectionInfo,
  useBuildTopicTree,
} from "../hooks/useBuildTopicTree";
import { getExpandedItemIds } from "@/components/filters/TreeSelect/utils";
import {
  buildTreeViewItems,
  collectFullySelectedCollections,
  fromTopicNodeId,
  toCollectionNodeId,
} from "../utils/topicTreeView";
import { TopicsSearchTab } from "./topics/TopicsSearchTab";
import { TopicsSelectedTab } from "./topics/TopicsSelectedTab";
import {
  useTopicsStateContext,
  type TopicsState,
} from "../hooks/useTopicsState";

const SEARCH_CONSTANTS = {
  MIN_FILTER_LENGTH: 3,
  DEBOUNCE_DELAY: 300,
};

export interface TopicsSectionProps {
  hideWrapper?: boolean;
}

// Inner component that wraps TopicsSection with store access (filter sidebar).
const TopicsSectionWithStore: FC<TopicsSectionProps> = (props) => {
  const slice = useCompanyFilterSlice("intent");
  const { setSlice } = useCompanyFilterActions();
  return (
    <TopicsSectionInner
      {...props}
      topicIds={slice.topicIds}
      topicsCache={slice.topicsCache}
      collectionId={slice.collectionId}
      collectionName={slice.collectionName}
      topicsInclusionMethod={slice.topicsInclusionMethod}
      patchState={(patch) => setSlice("intent", { ...slice, ...patch })}
    />
  );
};

// Inner component that wraps TopicsSection with local context (scoring editors).
const TopicsSectionWithContext: FC<
  TopicsSectionProps & {
    ctx: NonNullable<ReturnType<typeof useTopicsStateContext>>;
  }
> = ({ ctx, ...props }) => {
  return (
    <TopicsSectionInner
      {...props}
      topicIds={ctx.topicIds}
      topicsCache={ctx.topicsCache}
      collectionId={ctx.collectionId}
      collectionName={ctx.collectionName}
      topicsInclusionMethod={ctx.topicsInclusionMethod}
      patchState={ctx.update}
    />
  );
};

export const TopicsSection: FC<TopicsSectionProps> = memo((props) => {
  const controlled = useTopicsStateContext();
  if (controlled)
    return <TopicsSectionWithContext {...props} ctx={controlled} />;
  return <TopicsSectionWithStore {...props} />;
});

interface TopicsSectionInnerProps extends TopicsSectionProps {
  topicIds: string[];
  topicsCache: CachedTopic[];
  collectionId: string | null;
  collectionName: string | null;
  topicsInclusionMethod: "ANY_PRESENT" | "ALL_PRESENT";
  patchState: (patch: Partial<TopicsState>) => void;
}

const TopicsSectionInner: FC<TopicsSectionInnerProps> = ({
  hideWrapper = false,
  topicIds,
  topicsCache: topicsCacheVal,
  collectionId: collectionIdVal,
  collectionName: collectionNameVal,
  topicsInclusionMethod: inclusionMethodVal,
  patchState,
}) => {
  const { showWarning } = useSnackbar();

  // ── Local UI state ──────────────────────────────────────────────────

  const [topicsExpanded, setTopicsExpanded] = useState(true);
  const [topicsTab, setTopicsTab] = useState<
    "results" | "collections" | "selected"
  >("results");

  const [inputValue, setInputValue] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("");
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [matchMenuAnchor, setMatchMenuAnchor] = useState<HTMLElement | null>(
    null
  );

  const { handleDownload } = useBulkTopicOperations();

  const trimmedInput = inputValue.trim();
  const debouncedTrimmedInput = useDebouncedValue(
    trimmedInput,
    SEARCH_CONSTANTS.DEBOUNCE_DELAY
  );
  const searchQuery =
    trimmedInput.length < SEARCH_CONSTANTS.MIN_FILTER_LENGTH
      ? ""
      : debouncedTrimmedInput;

  useEffect(() => {
    if (
      !collectionIdVal &&
      trimmedInput.length >= SEARCH_CONSTANTS.MIN_FILTER_LENGTH
    ) {
      setTopicsTab("results");
    }
  }, [collectionIdVal, trimmedInput]);

  useEffect(() => {
    if (collectionIdVal && topicsTab === "results") {
      setTopicsTab("collections");
    }
  }, [collectionIdVal, topicsTab]);

  const shouldSearch = searchQuery.length >= SEARCH_CONSTANTS.MIN_FILTER_LENGTH;

  const { data: collectionTreeData } = useGetIntentTopicCollectionTreeQuery({
    context: { clientName: TAXONOMY_CLIENT },
    skip: !shouldSearch,
  });

  const collections = useMemo((): CollectionInfo[] => {
    if (!collectionTreeData?.intentTopicCollectionTree?.data) return [];
    return collectionTreeData.intentTopicCollectionTree.data
      .filter(
        (c): c is NonNullable<typeof c> =>
          c !== null && c.id !== null && c.displayName !== null
      )
      .map((c) => ({
        id: c.id as string,
        displayName: c.displayName as string,
        parentId: (c.parentId as string) ?? null,
      }));
  }, [collectionTreeData]);

  const { data: searchData, loading: searchLoading } =
    useSearchIntentTopicsQuery({
      variables: { displayName: searchQuery },
      skip: !shouldSearch,
      context: { clientName: TAXONOMY_CLIENT },
    });

  const searchResults = useMemo((): CachedTopic[] => {
    if (!searchData?.intentTopic?.data) return [];
    return searchData.intentTopic.data
      .filter(
        (topic): topic is NonNullable<typeof topic> =>
          topic !== null && topic.id !== null && topic.displayName !== null
      )
      .map((topic) => ({
        id: topic.id as string,
        displayName: topic.displayName as string,
        category: topic.category ?? undefined,
        collectionTree: (topic.collectionTree ?? []).filter(
          (t): t is string => t !== null && t !== "0"
        ),
      }));
  }, [searchData]);

  const rootTopics = useMemo(
    () => searchResults.filter((topic) => topic.collectionTree.length === 0),
    [searchResults]
  );

  const topicsWithCollections = useMemo(
    () => searchResults.filter((topic) => topic.collectionTree.length > 0),
    [searchResults]
  );

  const searchTrees = useBuildTopicTree(topicsWithCollections, collections);

  const { items: searchItems, topicNodeMap } = useMemo(
    () => buildTreeViewItems(searchTrees, rootTopics),
    [searchTrees, rootTopics]
  );

  const searchExpandedItems = useMemo(
    () => getExpandedItemIds(searchItems),
    [searchItems]
  );

  const searchTopicIds = useMemo(
    () => new Set(searchResults.map((topic) => topic.id)),
    [searchResults]
  );

  const selectedTopicIdsInSearch = useMemo(
    () => topicIds.filter((id) => searchTopicIds.has(id)),
    [topicIds, searchTopicIds]
  );

  const selectedTopicSetInSearch = useMemo(
    () => new Set(selectedTopicIdsInSearch),
    [selectedTopicIdsInSearch]
  );

  const fullySelectedCollectionIds = useMemo(
    () =>
      collectFullySelectedCollections(searchTrees, selectedTopicSetInSearch),
    [searchTrees, selectedTopicSetInSearch]
  );

  const treeSelectedItems = useMemo(() => {
    const topicNodeIds = selectedTopicIdsInSearch.flatMap(
      (id) => topicNodeMap.get(id) ?? []
    );
    return [
      ...topicNodeIds,
      ...fullySelectedCollectionIds.map(toCollectionNodeId),
    ];
  }, [selectedTopicIdsInSearch, fullySelectedCollectionIds, topicNodeMap]);

  const selectedTopicsWithFallbackNames = useMemo(() => {
    const cacheById = new Map(topicsCacheVal.map((t) => [t.id, t]));
    return topicIds.map((id) => {
      const cached = cacheById.get(id);
      if (cached) return cached;
      return {
        id,
        displayName: `Topic ${id.slice(0, 8)}...`,
        category: undefined,
        collectionTree: [],
      };
    });
  }, [topicIds, topicsCacheVal]);

  const filteredSelectedTopics = useMemo(() => {
    const search = selectedFilter.trim().toLowerCase();
    if (!search) return selectedTopicsWithFallbackNames;
    return selectedTopicsWithFallbackNames.filter((topic) =>
      topic.displayName.toLowerCase().includes(search)
    );
  }, [selectedTopicsWithFallbackNames, selectedFilter]);

  // ── Handlers ────────────────────────────────────────────────────────

  const showTopicLimitSnackbar = useCallback(
    (options?: { added?: number; attempted?: number }) => {
      if (options?.added !== undefined && options?.attempted !== undefined) {
        showWarning(
          `Only ${options.added.toLocaleString()} of ${options.attempted.toLocaleString()} topics were added. Intent topics are limited to ${MAX_INTENT_TOPICS.toLocaleString()}.`
        );
        return;
      }
      showWarning(
        `You can select up to ${MAX_INTENT_TOPICS.toLocaleString()} intent topics. Remove some topics to add more.`
      );
    },
    [showWarning]
  );

  const handleTopicSelect = useCallback(
    (topic: CachedTopic, resetInput = true) => {
      if (topicIds.includes(topic.id)) return;
      if (topicIds.length >= MAX_INTENT_TOPICS) {
        showTopicLimitSnackbar();
        return;
      }
      patchState({
        collectionId: null,
        collectionName: null,
        topicIds: [...topicIds, topic.id],
        topicsCache: [...topicsCacheVal, topic],
      });
      if (resetInput) setInputValue("");
    },
    [patchState, showTopicLimitSnackbar, topicIds, topicsCacheVal]
  );

  const handleTopicRemove = useCallback(
    (topicId: string) => {
      patchState({
        collectionId: null,
        collectionName: null,
        topicIds: topicIds.filter((id) => id !== topicId),
        topicsCache: topicsCacheVal.filter((t) => t.id !== topicId),
      });
    },
    [patchState, topicIds, topicsCacheVal]
  );

  const handleSearchSelectionChange = useCallback(
    (itemIds: string[]) => {
      const nextSearchTopicIds = itemIds
        .filter((id) => id.startsWith("topic:"))
        .map(fromTopicNodeId);
      const outsideSearchIds = topicIds.filter((id) => !searchTopicIds.has(id));
      const combinedIds = Array.from(
        new Set([...outsideSearchIds, ...nextSearchTopicIds])
      );

      if (
        combinedIds.length > MAX_INTENT_TOPICS &&
        combinedIds.length > topicIds.length
      ) {
        showTopicLimitSnackbar();
        return;
      }

      const cacheById = new Map(topicsCacheVal.map((t) => [t.id, t]));
      const searchById = new Map(searchResults.map((t) => [t.id, t]));
      const nextCache = combinedIds
        .map((id) => cacheById.get(id) ?? searchById.get(id))
        .filter((t): t is CachedTopic => !!t);

      patchState({
        collectionId: null,
        collectionName: null,
        topicIds: combinedIds,
        topicsCache: nextCache,
      });
    },
    [
      searchResults,
      searchTopicIds,
      patchState,
      showTopicLimitSnackbar,
      topicIds,
      topicsCacheVal,
    ]
  );

  const handleClearTopics = useCallback(() => {
    patchState({
      collectionId: null,
      collectionName: null,
      topicIds: [],
      topicsCache: [],
    });
  }, [patchState]);

  const handleClearCollection = useCallback(() => {
    patchState({
      collectionId: null,
      collectionName: null,
      topicIds: [],
      topicsCache: [],
    });
  }, [patchState]);

  const handleApplyCollection = useCallback(
    (option: CollectionSelectOption) => {
      const nextTopicIdsRaw = option.ids as string[];
      const nextTopicIds = nextTopicIdsRaw.slice(0, MAX_INTENT_TOPICS);
      if (nextTopicIds.length < nextTopicIdsRaw.length) {
        showTopicLimitSnackbar({
          added: nextTopicIds.length,
          attempted: nextTopicIdsRaw.length,
        });
      }
      patchState({
        collectionId: String(option.value),
        collectionName: option.label,
        topicIds: nextTopicIds,
        topicsCache: [],
      });
    },
    [patchState, showTopicLimitSnackbar]
  );

  const handleUploadSuccess = useCallback(
    (uploadedTopics: CachedTopic[]) => {
      const existingIds = new Set(topicIds);
      const newTopics = uploadedTopics.filter(
        (topic) => !existingIds.has(topic.id)
      );
      if (newTopics.length === 0) return;

      const remainingSlots = MAX_INTENT_TOPICS - topicIds.length;
      if (remainingSlots <= 0) {
        showTopicLimitSnackbar({ added: 0, attempted: newTopics.length });
        return;
      }

      const topicsToAdd = newTopics.slice(0, remainingSlots);
      if (topicsToAdd.length < newTopics.length) {
        showTopicLimitSnackbar({
          added: topicsToAdd.length,
          attempted: newTopics.length,
        });
      }

      patchState({
        collectionId: null,
        collectionName: null,
        topicIds: [...topicIds, ...topicsToAdd.map((t) => t.id)],
        topicsCache: [...topicsCacheVal, ...topicsToAdd],
      });
    },
    [patchState, showTopicLimitSnackbar, topicIds, topicsCacheVal]
  );

  const handleDownloadClick = useCallback(() => {
    handleDownload(topicsCacheVal);
  }, [handleDownload, topicsCacheVal]);

  const handleClearInput = useCallback(() => {
    setInputValue("");
  }, []);

  // ── Shared content (tabs + panels + inclusion method) ───────────────

  const content = (
    <>
      <Box
        sx={
          hideWrapper
            ? { borderBottom: 1, borderColor: "divider" }
            : { mx: -2, borderBottom: 1, borderColor: "divider" }
        }
      >
        <Tabs
          value={topicsTab}
          onChange={(_event, value: "results" | "collections" | "selected") => {
            if (value === "results" && collectionIdVal) return;
            setTopicsTab(value);
          }}
          variant="fullWidth"
          sx={{
            minHeight: 40,
            "& .MuiTab-root": { minHeight: 40, textTransform: "none" },
          }}
        >
          <Tab
            value="results"
            icon={<SearchIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={
              <Tooltip
                title="Clear the selected collection to enable topic search."
                disableHoverListener={!collectionIdVal}
              >
                <span>Search</span>
              </Tooltip>
            }
            disabled={Boolean(collectionIdVal)}
            {...(collectionIdVal ? { component: "div" as const } : {})}
            sx={
              collectionIdVal
                ? {
                    opacity: 0.55,
                    cursor: "not-allowed",
                    "&.Mui-disabled": { pointerEvents: "auto" },
                  }
                : undefined
            }
          />
          <Tab
            value="collections"
            icon={<FolderOutlinedIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Collections"
          />
          <Tab
            value="selected"
            icon={
              <Chip
                label={
                  topicIds.length > 999
                    ? `${Math.floor(topicIds.length / 1000)}k`
                    : topicIds.length
                }
                size="small"
                sx={{
                  height: 18,
                  minWidth: 18,
                  fontSize: "0.65rem",
                  bgcolor: topicIds.length > 0 ? "primary.main" : "grey.400",
                  color: "white",
                }}
              />
            }
            iconPosition="start"
            label="Selected"
          />
        </Tabs>
      </Box>

      {topicsTab === "results" && (
        <TopicsSearchTab
          inputValue={inputValue}
          onInputChange={setInputValue}
          onClearInput={handleClearInput}
          minChars={SEARCH_CONSTANTS.MIN_FILTER_LENGTH}
          shouldSearch={shouldSearch}
          searchQuery={searchQuery}
          searchLoading={searchLoading}
          resultsCount={searchResults.length}
          searchItems={searchItems}
          treeSelectedItems={treeSelectedItems}
          searchExpandedItems={searchExpandedItems}
          onSelectionChange={handleSearchSelectionChange}
          selectedTopicIds={topicIds}
          onAddSuggestedTopic={(topic) => handleTopicSelect(topic, false)}
        />
      )}

      {topicsTab === "collections" && (
        <Box
          sx={{
            py: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Apply intent topics from a saved collection. This replaces the
            current selection.
          </Typography>

          {collectionIdVal ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Selected:
              </Typography>
              <Chip
                size="small"
                label={collectionNameVal ?? "Loading..."}
                onDelete={handleClearCollection}
                sx={{ maxWidth: 320 }}
              />
            </Box>
          ) : (
            <CollectionSelect
              placeholder="Search intent topic collections"
              type="intent"
              onSelect={handleApplyCollection}
            />
          )}
        </Box>
      )}

      {topicsTab === "selected" && (
        <TopicsSelectedTab
          selectedFilter={selectedFilter}
          onSelectedFilterChange={setSelectedFilter}
          onClearSelectedFilter={() => setSelectedFilter("")}
          totalSelectedCount={topicIds.length}
          filteredTopics={filteredSelectedTopics}
          onRemoveTopic={handleTopicRemove}
          showClearAll={topicIds.length > 0}
          onClearAll={handleClearTopics}
        />
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
          py: 1.5,
          mt: 1.5,
          ...(hideWrapper
            ? {
                px: 1.5,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "grey.50",
              }
            : {
                mx: -2,
                px: 2,
                borderTop: 1,
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: "grey.50",
              }),
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Show companies matching
        </Typography>
        <Chip
          label={inclusionMethodVal === "ANY_PRESENT" ? "any" : "all"}
          size="small"
          deleteIcon={<KeyboardArrowDownIcon sx={{ fontSize: 14 }} />}
          onDelete={(e) => setMatchMenuAnchor(e.currentTarget as HTMLElement)}
          onClick={(e) => setMatchMenuAnchor(e.currentTarget)}
          sx={{
            height: 20,
            fontSize: "0.75rem",
            fontWeight: 600,
            bgcolor: "primary.main",
            color: "white",
            cursor: "pointer",
            "& .MuiChip-deleteIcon": { color: "white", ml: -0.5 },
            "&:hover": { bgcolor: "primary.dark" },
          }}
        />
        <Typography variant="body2" color="text.secondary">
          of the selected topics
        </Typography>
        <Menu
          anchorEl={matchMenuAnchor}
          open={Boolean(matchMenuAnchor)}
          onClose={() => setMatchMenuAnchor(null)}
          slotProps={{ paper: { sx: { minWidth: 240, mt: 0.5 } } }}
        >
          <MenuItem
            selected={inclusionMethodVal === "ANY_PRESENT"}
            onClick={() => {
              patchState({ topicsInclusionMethod: "ANY_PRESENT" });
              setMatchMenuAnchor(null);
            }}
            sx={{ py: 1.25 }}
          >
            <Stack>
              <Typography variant="body2" fontWeight={600}>
                Any
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  — at least one topic
                </Typography>
              </Typography>
            </Stack>
          </MenuItem>
          <MenuItem
            selected={inclusionMethodVal === "ALL_PRESENT"}
            onClick={() => {
              patchState({ topicsInclusionMethod: "ALL_PRESENT" });
              setMatchMenuAnchor(null);
            }}
            sx={{ py: 1.25 }}
          >
            <Stack>
              <Typography variant="body2" fontWeight={600}>
                All
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  — every single topic
                </Typography>
              </Typography>
            </Stack>
          </MenuItem>
        </Menu>
      </Box>
    </>
  );

  // ── Render ──────────────────────────────────────────────────────────

  if (hideWrapper) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={700}
          sx={{ mb: 1, display: "block" }}
        >
          TOPICS (REQUIRED)
        </Typography>
        {content}
      </Box>
    );
  }

  return (
    <>
      <AccordionSection
        title="Topics"
        expanded={topicsExpanded}
        onToggle={() => setTopicsExpanded((prev) => !prev)}
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip
              title={
                collectionIdVal
                  ? "Clear the selected collection to enable upload."
                  : "Upload topics from CSV"
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={Boolean(collectionIdVal)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsUploadDialogOpen(true);
                  }}
                >
                  <UploadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                topicIds.length === 0
                  ? "Select topics to download"
                  : "Download selected topics"
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={topicIds.length === 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadClick();
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        }
        contentSx={{ py: 0 }}
      >
        {content}
      </AccordionSection>

      <TopicUploadDialog
        open={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        onSuccess={handleUploadSuccess}
      />
    </>
  );
};

TopicsSection.displayName = "TopicsSection";
